// File: src/components/ImageUpload.tsx
// FINAL CLEAN VERSION. Uses central API and inherits styles from the theme.

import React, {useCallback, useState} from 'react';
import {Alert, Box, Button, Card, CardMedia, IconButton, LinearProgress, Paper, Typography} from '@mui/material';
import {Add as AddIcon, CloudUpload as UploadIcon, Delete as DeleteIcon} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {uploadImages} from '../services/api'; // Import our new API function

interface ImageUploadProps {
    images: string[];
    onImagesChange: (images: string[]) => void;
    maxImages?: number;
}

const ImageUpload: React.FC<ImageUploadProps> = ({images, onImagesChange, maxImages = 10}) => {
    const {t} = useTranslation();
    const [isUploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleFileSelect = useCallback(async (files: FileList) => {
        if (files.length === 0) return;
        if (images.length + files.length > maxImages) {
            setError(t('image_upload.max_images_error', { maxImages }));
            return;
        }
        setUploading(true);
        setError(null);
        try {
            const uploadedFiles = await uploadImages(files);
            const newImageUrls = uploadedFiles.map(file => file.url);
            onImagesChange([...images, ...newImageUrls]);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('image_upload.upload_failed'));
        } finally {
            setUploading(false);
        }
    }, [images, onImagesChange, maxImages, t]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        handleFileSelect(e.dataTransfer.files);
    }, [handleFileSelect]);

    const handleDragOver = useCallback((e: React.DragEvent) => e.preventDefault(), []);

    const handleRemoveImage = (indexToRemove: number) =>
        onImagesChange(images.filter((_, index) => index !== indexToRemove));

    return (
        <Box>
            <Typography variant="h6" sx={{mb: 2}}>
                {t('image_upload.title', { current: images.length, max: maxImages })}
            </Typography>

            <Paper
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                sx={{p: 3, textAlign: 'center', border: '2px dashed', borderColor: 'divider', cursor: 'pointer', mb: 2}}
                onClick={() => document.getElementById('image-upload-input')?.click()}
            >
                <input
                    id="image-upload-input"
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => e.target.files && handleFileSelect(e.target.files)}
                />
                <UploadIcon sx={{fontSize: 48, color: 'text.secondary', mb: 1}}/>
                <Typography>
                    {t('image_upload.drag_drop_text')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {t('image_upload.file_limits', { maxImages, maxSize: '5MB' })}
                </Typography>
            </Paper>

            {isUploading && <LinearProgress color="primary" sx={{mb: 2}}/>}

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{mb: 2}}>
                    {error}
                </Alert>
            )}

            <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(100px, 1fr))" gap={2}>
                {images.map((url, index) => (
                    <Card key={index} sx={{position: 'relative'}}>
                        <CardMedia component="img" image={url} sx={{height: 100, objectFit: 'cover'}}/>
                        <IconButton
                            size="small"
                            onClick={() => handleRemoveImage(index)}
                            aria-label={t('image_upload.remove_image')}
                            sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: 'rgba(0,0,0,0.7)',
                                '&:hover': {bgcolor: 'rgba(0,0,0,0.9)'}
                            }}
                        >
                            <DeleteIcon fontSize="small" color="error"/>
                        </IconButton>
                    </Card>
                ))}
            </Box>

            {images.length > 0 && images.length < maxImages && (
                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AddIcon/>}
                    onClick={() => document.getElementById('image-upload-input')?.click()}
                    disabled={isUploading}
                    sx={{mt: 2}}
                >
                    {t('image_upload.add_more_images')}
                </Button>
            )}
        </Box>
    );
};

export default ImageUpload;
